import React, { useState, useEffect, useRef } from "react";
import {
  FaTimes,
  FaCoins,
  FaExclamationTriangle,
  FaSpinner,
  FaCheckCircle,
} from "react-icons/fa";
import apiRequest from "../services/api";

const PAYMENT_METHODS = [
  { value: "CASH", label: "Cash" },
  { value: "BANK", label: "Bank Transfer" },
  { value: "NPG", label: "Direct Online Payment (Bank)" },
  { value: "CHAPA", label: "Direct Online Payment (Wallet)" },
];

export default function InvoicePaymentModal({
  isOpen,
  invoice,
  onClose,
  onSuccess,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    amount: "",
    change: "0.00",
    method: "BANK",
    reference: "",
    whReceipt: "",
    vwhReceipt: "",
    date: new Date().toISOString().slice(0, 16), // datetime-local format
    bankCommission: "0.00",
  });

  const lastAutoRef = useRef("0.00");

  const balance = Number(invoice?.balanceAmount || 0);
  const isSale = invoice?.type === "SALE";
  const isPurchase = invoice?.type === "PURCHASE";

  const hasWithhold =
    isSale &&
    Boolean(invoice?.applyWithhold) &&
    Number(invoice?.withhold || 0) > 0;

  const hasVatWithhold =
    isSale &&
    Boolean(invoice?.applyVatWithhold) &&
    Number(invoice?.vatWithhold || 0) > 0;

  // Show bank commission only for PURCHASE + BANK
  const showBankCommission = isPurchase && form.method === "BANK";

  useEffect(() => {
    if (!isOpen || !invoice) return;

    setError("");

    const autoVal = balance.toFixed(2);
    lastAutoRef.current = autoVal;

    setForm({
      amount: autoVal,
      change: "0.00",
      method: "BANK",
      reference: "",
      whReceipt: "",
      vwhReceipt: "",
      date: new Date().toISOString().slice(0, 16),
      bankCommission: "0.00",
    });
  }, [isOpen, invoice?.id, balance]);

  if (!isOpen || !invoice) return null;

  const isCash = form.method === "CASH";
  const isBank = form.method === "BANK";
  const isChapa = form.method === "CHAPA";
  const isNpg = form.method === "NPG";

  const enteredAmount = parseFloat(form.amount) || 0;
  const changeAmount = isCash ? Math.max(0, enteredAmount - balance) : 0;

  // ---------------------------------------------------------
  // CHAPA CHECKOUT
  // ---------------------------------------------------------
  const startChapaCheckout = async (requestBody) => {
    if (!invoice?.id) {
      setError("Invoice is required.");
      return;
    }
    if (enteredAmount <= 0) {
      setError("Amount must be greater than zero.");
      return;
    }
    if (enteredAmount > balance) {
      setError("Amount cannot exceed balance.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const res = await apiRequest(`payments/chapa/checkout/${invoice.id}`, {
        method: "POST",
        body: JSON.stringify(requestBody),
      });

      const checkoutUrl = res?.data?.checkoutUrl || "";
      if (!checkoutUrl) {
        setError(res?.message || "Failed to create Chapa checkout.");
        return;
      }
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err?.message || "Could not start Chapa payment.");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------------------------------------------------
  // NPG CHECKOUT
  // ---------------------------------------------------------
  const startNpgCheckout = async (requestBody) => {
    if (!invoice?.id) {
      setError("Invoice is required.");
      return;
    }
    if (enteredAmount <= 0) {
      setError("Amount must be greater than zero.");
      return;
    }
    if (enteredAmount > balance) {
      setError("Amount cannot exceed balance.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const res = await apiRequest(`payments/npg/checkout/${invoice.id}`, {
        method: "POST",
        body: JSON.stringify(requestBody),
      });

      const checkoutUrl = res?.data?.checkoutUrl || "";
      if (!checkoutUrl) {
        setError(res?.message || "Failed to create Bank checkout.");
        return;
      }
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err?.message || "Could not start Bank payment.");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------------------------------------------------
  // NORMAL PAYMENT
  // ---------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (enteredAmount <= 0) {
      setError("Amount must be greater than zero.");
      return;
    }

    if (enteredAmount > balance && !isCash) {
      setError("Amount cannot exceed balance.");
      return;
    }

    // bankCommission is 0 unless it's a PURCHASE paid by BANK
    const bankCommissionValue =
      isPurchase && form.method === "BANK"
        ? parseFloat(form.bankCommission) || 0
        : 0;

    if (bankCommissionValue < 0) {
      setError("Bank commission cannot be negative.");
      return;
    }

    const requestBody = {
      amount: enteredAmount,
      change: isCash ? changeAmount : 0,
      method: form.method,
      reference: form.reference || "",
      date: form.date
        ? new Date(form.date).toISOString()
        : new Date().toISOString(),
      bankCommission: bankCommissionValue,

      // Withholding receipts only for SALE
      whReceipt: isSale && hasWithhold ? form.whReceipt || "" : "",
      vwhReceipt: isSale && hasVatWithhold ? form.vwhReceipt || "" : "",
    };

    const onlineRequestBody = {
      amount: enteredAmount,
      date: form.date
        ? new Date(form.date).toISOString()
        : new Date().toISOString(),
      bankCommission: 0, // online methods never carry bank commission here
      whReceipt: isSale && hasWithhold ? form.whReceipt || "" : "",
      vwhReceipt: isSale && hasVatWithhold ? form.vwhReceipt || "" : "",
    };

    // Chapa
    if (isChapa) {
      await startChapaCheckout(onlineRequestBody);
      return;
    }

    // NPG
    if (isNpg) {
      await startNpgCheckout(onlineRequestBody);
      return;
    }

    // Normal payment
    setIsLoading(true);

    try {
      await apiRequest(`invoices/${invoice.id}/pay`, {
        method: "PUT",
        body: JSON.stringify(requestBody),
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Payment failed."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
      {/* Modal */}
      <div className="relative w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl p-5 sm:p-7 shadow-2xl text-slate-100 flex flex-col gap-5">
        {/* Header */}
        <div className="flex justify-between items-center pb-3 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
              <FaCoins className="text-emerald-400 text-lg" />
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-bold">Record Payment</h3>
              <p className="text-xs text-slate-400 font-mono">
                {invoice.invoiceNumber}
              </p>
            </div>
          </div>

          <button
            type="button"
            disabled={isLoading}
            onClick={onClose}
            className="p-2 rounded-xl bg-slate-800 text-slate-400 hover:text-white transition"
          >
            <FaTimes className="text-xs" />
          </button>
        </div>

        {/* Financial Summary */}
        <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-4 font-mono text-xs space-y-1.5">
          <div className="flex justify-between text-slate-400">
            <span>Subtotal</span>
            <span>
              {Number(invoice.subtotal || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}{" "}
              ETB
            </span>
          </div>

          {Boolean(invoice.applyVat) && Number(invoice.tax || 0) > 0 && (
            <div className="flex justify-between text-slate-400">
              <span>{invoice.taxType === "TOT" ? "TOT" : "VAT"}</span>
              <span className="text-emerald-400">
                +
                {Number(invoice.tax || 0).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                })}{" "}
                ETB
              </span>
            </div>
          )}

          <div className="flex justify-between font-bold text-slate-100 pt-1 border-t border-dashed border-slate-700">
            <span>Invoice Total</span>
            <span>
              {Number(invoice.totalAmount || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}{" "}
              ETB
            </span>
          </div>

          {Boolean(invoice.applyWithhold) &&
            Number(invoice.withhold || 0) > 0 && (
              <div className="flex justify-between text-slate-400">
                <span>Income Withholding</span>
                <span className="text-amber-400">
                  -
                  {Number(invoice.withhold || 0).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                  })}{" "}
                  ETB
                </span>
              </div>
            )}

          {Boolean(invoice.applyVatWithhold) &&
            Number(invoice.vatWithhold || 0) > 0 && (
              <div className="flex justify-between text-slate-400">
                <span>VAT Withholding</span>
                <span className="text-purple-400">
                  -
                  {Number(invoice.vatWithhold || 0).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                  })}{" "}
                  ETB
                </span>
              </div>
            )}

          <div className="flex justify-between text-slate-400 pt-1 border-t border-dashed border-slate-700">
            <span>Already Paid</span>
            <span>
              {Number(invoice.paidAmount || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}{" "}
              ETB
            </span>
          </div>

          <div className="flex justify-between font-bold text-sm pt-1">
            <span>Balance (Cash Payable)</span>
            <span
              className={balance > 0 ? "text-rose-400" : "text-emerald-400"}
            >
              {balance.toLocaleString("en-US", { minimumFractionDigits: 2 })}{" "}
              ETB
            </span>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-rose-950/50 border border-rose-800/80 rounded-xl text-rose-300 text-xs flex items-center gap-2">
            <FaExclamationTriangle className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          {/* Payment Method */}
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
              Payment Method
            </label>
            <select
              value={form.method}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  method: e.target.value,
                  change: e.target.value === "CASH" ? p.change : "0.00",
                  reference: e.target.value === "CHAPA" ? "" : p.reference,
                  // reset commission when switching away from BANK
                  bankCommission:
                    e.target.value === "BANK" && isPurchase
                      ? p.bankCommission
                      : "0.00",
                }))
              }
              disabled={isLoading}
              className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-slate-100 focus:outline-none focus:border-emerald-500/50"
            >
              {PAYMENT_METHODS.map((m) => {
                if (
                  invoice?.type === "PURCHASE" &&
                  (m.value === "NPG" || m.value === "CHAPA")
                ) {
                  return null;
                }
                return (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Payment Date */}
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
              Payment Date
            </label>
            <input
              type="datetime-local"
              disabled={isLoading}
              value={form.date}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  date: e.target.value,
                }))
              }
              className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-slate-100 focus:outline-none focus:border-emerald-500/50"
            />
          </div>

          {/* Amount */}
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
              Amount (ETB) *
            </label>
            <div className="relative">
              <FaCoins className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              <input
                type="number"
                step="0.01"
                min="0.01"
                required
                disabled={isLoading}
                value={form.amount}
                onChange={(e) => {
                  setForm((p) => ({
                    ...p,
                    amount: e.target.value,
                  }));
                  lastAutoRef.current = e.target.value;
                }}
                className="w-full bg-slate-950 border border-slate-700 rounded-xl pl-9 pr-3 py-2.5 font-mono font-bold text-emerald-400 focus:outline-none focus:border-emerald-500/50"
              />
            </div>

            {isChapa && (
              <p className="text-[10px] text-slate-500">
                You will be redirected to Wallet to complete this amount.
              </p>
            )}
            {isNpg && (
              <p className="text-[10px] text-slate-500">
                You will be redirected to Bank to complete this amount.
              </p>
            )}
          </div>

          {/* Bank Commission – only for PURCHASE + BANK */}
          {showBankCommission && (
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
                Bank Commission (ETB)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                disabled={isLoading}
                value={form.bankCommission}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    bankCommission: e.target.value,
                  }))
                }
                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 font-mono text-slate-100 focus:outline-none focus:border-emerald-500/50"
                placeholder="0.00"
              />
            </div>
          )}

          {/* Change */}
          {isCash && changeAmount > 0 && (
            <div className="flex justify-between items-center bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2.5">
              <span className="text-amber-400 font-medium">Change</span>
              <span className="font-mono font-bold text-amber-300">
                {changeAmount.toFixed(2)} ETB
              </span>
            </div>
          )}

          {/* Payment Reference */}
          {!isChapa && !isNpg && (
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
                Payment Reference
              </label>
              <input
                type="text"
                disabled={isLoading}
                placeholder="e.g. FT26156XXXXX"
                value={form.reference}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    reference: e.target.value,
                  }))
                }
                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-emerald-500/50"
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-3 border-t border-slate-800">
            <button
              type="button"
              disabled={isLoading}
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-slate-300 font-semibold hover:bg-slate-700 transition"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold flex items-center gap-2 transition"
            >
              {isLoading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Processing…
                </>
              ) : isChapa ? (
                <>
                  <FaCheckCircle />
                  <span>Pay with Chapa</span>
                </>
              ) : isNpg ? (
                <>
                  <FaCheckCircle />
                  <span>Pay with Bank</span>
                </>
              ) : (
                <>
                  <FaCheckCircle />
                  <span>Record Payment</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
